"use client";

import React, { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import styled, { useTheme } from "styled-components";
import {
  navItems,
  patientPathLinks,
  insightsLinks,
  settingsLinks,
  INavItem,
  subSettingsLinks,
  mobileSettingsLink,
} from "../../constants/navItems";
import { useParams, usePathname, useRouter } from "next/navigation";
import DePuySynthesLogo from "@/assets/logos/DePuySynthes.svg";
import Button from "../Button/Button";
import UploadIcon from "@/assets/icons/UploadIcon.svg";
import DownloadIcon from "@/assets/icons/DownloadIcon.svg";
import MenuIcon from "@/assets/icons/MenuIcon.svg";
import AddIcon from "@/assets/icons/AddIcon.svg";
import LogoutIcon from "@/assets/icons/LogoutIcon.svg";
import EditIcon from "@/assets/icons/EditIcon.svg";
import ReturnIcon from "@/assets/icons/ReturnIcon.svg";
import CloseIcon from "@/assets/icons/CloseIcon.svg";
import ChevronIcon from "@/assets/icons/Chevron.svg";
import FilterIcon from "@/assets/icons/FilterIcon.svg";
import { useMedia } from "@/lib/hooks/useMedia";
import { Icon } from "../Icon/Icon";
import Image from "next/image";
import { useBoundStore } from "@/lib/stores/useBoundStore";
import { useAuth } from "react-oidc-context";
import UploadCaseDataContent from "../Modal/UploadCaseDataContent";
import { Modal } from "../Modal/Modal";
import { ShowCaseUploadToast } from "../ToastUtils/UploadToast";
import { useCaseUpload } from "@/lib/hooks/useCaseUpload";
import { useDownloadFiles } from "@/lib/hooks/useSurgery";
import { useGetOrg } from "@/lib/hooks/useOrg";
import toast from "react-hot-toast";
import { LocaleDropdown } from "../Dropdown/LocaleDropdown";
import { SUPPORTED_LOCALES } from "@/lib/constants/locale";
import Notifications from "../Notifications/Notifications";
import PatientHeadNav from "./PatientHeadNav";
import AdminHeadNav from "./AdminHeadNav";
import { useSessionStore } from "@/lib/stores/useSessionStore";
import { useAuthStore } from "@/lib/stores/useAuthStore";
import MultiOrgDropdown from "../MultiOrgDropdown/MultiOrgDropdown";
import { useUserProfile } from "@/lib/hooks/useUser";
import { BLOCKED_PATIENTPATH_COUNTRIES } from "@/lib/constants/blockedPatientPath";

//#region styled
const NavContainer = styled.div`
  width: 100vw;
  display: flex;
  flex-direction: column;
  gap: 0;

  .navlinks {
    display: flex;
    // gap: 50px;
    flex-direction: row;
    align-items: center;

    Image {
      height: 25px;
      width: auto;
    }
  }

  .secondary-functions {
    display: flex;
    align-items: center;
    gap: 15px;
    padding-right: 30px;
  }

  .interactive {
    display: flex;
    gap: 25px;
  }

  .mobile-container {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .modal-mobile-container {
    display: flex;
    align-items: center;
    gap: 20px;
    h2 {
      font-weight: normal;
      font-size: 1.8rem;
      color: ${(props) => props.theme.main};
    }
  }

  @media (max-width: 1024px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10;
    background: white;
  }
`;

const NavigationContainer = styled.div<{ $bckgColor: string }>`
  background-color: ${(props) => props.$bckgColor};
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 1024px) {
    padding: 12.5px 20px;
  }
`;

const ClickableContainer = styled.div`
  padding: 15px 25px;
  cursor: pointer;

  @media (max-width: 1024px) {
    padding: 12.5px 15px;
  }
`;

const PrimaryItem = styled.div`
  display: flex;
  gap: 10px;
  color: ${({ theme }) => theme.main};
  border-bottom: 2px solid transparent;
  align-items: center;
  cursor: pointer;
  padding-bottom: 4px;
  position: relative;

  .nav-p {
    font-size: 1.25rem;
  }

  &.selected {
    border-color: ${({ theme }) => theme.main};
  }

  .settings-hovered {
    transform: rotate(180deg);
  }

  @media (max-width: 1024px) {
    color: ${({ theme }) => theme.black};
    padding: 10px 15px;
    border-bottom: 1px solid #cec9c2;

    img {
      filter: brightness(0);
    }

    &.selected {
      img {
        filter: none;
      }
      color: ${({ theme }) => theme.main};
    }
  }

  @media (max-width: 500px) {
    font-size: 1.15rem;
  }
`;

const SecondaryItem = styled.div`
  font-size: 0.875rem;
  font-weight: bold;
  cursor: pointer;
  color: ${({ theme }) => theme.black};

  &.selected {
    color: ${({ theme }) => theme.main};
  }

  @media (max-width: 1024px) {
    margin-left: 20px;
    font-size: 1.155rem;
    font-weight: normal;
  }

  @media (max-width: 500px) {
    font-size: 1rem;
  }
`;

const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 9998; /* Make sure this is below nav panel but above everything else */
`;

const MobileNavigationOverlay = styled.div`
  position: fixed;
  top: 0;
  min-width: fit-content;
  width: 40%;
  height: 100%;
  max-height: 100vh;
  overflow-y: auto;
  background: white;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
  z-index: 9999;

  &.filter {
    right: 0;
    transform: translateX(100%);

    .nav-p {
      font-size: 1.25rem;
      color: ${(props) => props.theme.main};
    }
  }

  &.menu {
    left: 0;
    transform: translateX(-100%);
  }

  &.open {
    transform: translateX(0);
  }

  .items {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .opened-chevron {
    transform: rotate(180deg);
  }
`;

const SettingsHover = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
  gap: 10px;
  border-radius: 5px;
  background: ${(props) => props.theme.white};
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  z-index: 100000;
  position: absolute;
  right: 260px;
  top: 48px;
  min-width: 100px;

  @media (max-width: 1253px) {
    top: 58px;
  }
`;

const MobileFilters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
  padding: 0 15px 15px 15px;
`;

export const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;

  .filter-container {
    display: flex;
    flex-direction: column;
    gap: 30px;
  }

  .filter-title {
    font-size: 1.25rem;
    font-weight: bold;
    margin-bottom: 20px;

    &:not(:first-of-type) {
      margin-top: 30px;
    }
  }
`;

const ModalMobileNavContainer = styled(NavigationContainer)`
  padding: 10px 20px;
  @media (max-width: 768px) {
    .modal-mobile-container h2 {
      font-size: 1.5rem;
    }
  }
`;
//#endregion

export default function HeadNavigation() {
  // route specific
  const params = useParams();
  const { locale, enrollmentId } = params;
  const router = useRouter();
  const pathname = usePathname();
  const auth = useAuth();
  const user_profile = useAuthStore((state) => state.user_profile);
  const userLicenses = useBoundStore((state) => state.userLicenses);
  const setUserLicenses = useBoundStore((state) => state.setUserLicenses);
  const hasInsightsLicense = !!(
    userLicenses && "jnj.velys.insights" in userLicenses
  );
  const hasPatientPathLicense = !!(
    userLicenses && "jnj.velys.patientpath" in userLicenses
  );
  const userCountry = useBoundStore((state) => state.userCountry);
  // Hide patient path actions where the module is unavailable by region or license.
  const canAddPatient =
    hasPatientPathLicense &&
    !BLOCKED_PATIENTPATH_COUNTRIES.includes(userCountry);

  const t = useTranslations();
  const tModals = useTranslations("modalTitles");
  const tTable = useTranslations("tables");
  const theme = useTheme();
  const [settingsHovered, setSettingsHovered] = useState(false);
  const filterComponent = useBoundStore((state) => state.filterComponent);

  // modal specific
  const isModalOpen = useBoundStore((state) => state.isModalOpen);
  const setModalType = useBoundStore((state) => state.setModalType);
  const modalType = useBoundStore((state) => state.modalType);
  const setModalOpen = useBoundStore((state) => state.setModalOpen);
  const viewingHelp = useBoundStore((state) => state.viewingHelp);

  // mobile navigation
  const max1024w = useMedia("(max-width: 1024px)");
  const max768w = useMedia("(max-width: 768px)");
  const isOverlay = useBoundStore((state) => state.overlayType);
  const setIsOverlay = useBoundStore((state) => state.setOverlayType);
  const [expandedNav, setExpandedNav] = useState<string | null>(null);

  // patient path specific
  const patientEnrollment = useBoundStore((state) => state.patientEnrollment);
  const setPatientEnrollment = useBoundStore(
    (state) => state.setPatientEnrollment
  );
  // const patientType = useBoundStore((state) => state.patientType);
  const setPatientType = useBoundStore((state) => state.setPatientType);
  const handleEditPatient = useBoundStore((state) => state.handleEditPatient);
  const handleAddPatient = useBoundStore((state) => state.handleAddPatient);
  const handleReturnToPatientPath = useBoundStore(
    (state) => state.handleReturnToPatientPath
  );
  const signOutRedirect = useBoundStore((state) => state.signOutRedirect);

  // upload case
  const setCaseUploadStatus = useBoundStore(
    (state) => state.setCaseUploadStatus
  );
  const uploadProgress = useBoundStore((state) => state.uploadProgress);
  const { uploadMutation } = useCaseUpload();
  const downloadMutation = useDownloadFiles();

  // settings Links
  const [updatedNavItems, setUpdatedNavItems] = useState(navItems);

  // patient modals
  const surgery = useBoundStore((state) => state.surgery);
  const handleCloseViewPatient = useBoundStore(
    (state) => state.handleCloseViewPatient
  );
  const downloadPatientCSV = useBoundStore((state) => state.downloadPatientCSV);

  // locale dropdown
  const selectedLocale = useBoundStore((state) => state.selectedLocale);
  const setSelectedLocale = useBoundStore((state) => state.setSelectedLocale);

  // get org profile
  const orgProfileQuery = useGetOrg(patientEnrollment?.org_id);

  // get user profile
  const { userProfileQuery } = useUserProfile();

  // check if session is active:
  const sessionExpired = useSessionStore((state) => state.sessionExpired);

  // set default locale
  useEffect(() => {
    if (typeof locale === "string" && locale) {
      setSelectedLocale(locale);
    }
  }, [locale]);

  // fetch userLicenses if we refresh the page and first load
  useEffect(() => {
    if (userProfileQuery.data) {
      setUserLicenses(userProfileQuery.data.licenses || {});
    }
  }, [userProfileQuery.data]);

  // set default patient type according to language
  useEffect(() => {
    setPatientType(t("componentText.viewActive"));
  }, [selectedLocale]);

  // update locale based on selectedLocale state
  useEffect(() => {
    if (selectedLocale) {
      const segments = pathname.split("/").filter(Boolean); // split the current path

      segments.shift(); // remove the first segment
      const newPath = "/" + segments.join("/");
      const targetPath = `/${selectedLocale}${newPath}`;

      // Only navigate if locale actually changed to avoid loop on initial mount
      if (!pathname.startsWith(`/${selectedLocale}`)) {
        window.location.href = targetPath;
      }
    }
  }, [selectedLocale]);

  useEffect(() => {
    const newNavItems = navItems
      .filter((item) => item.name !== "insights" || hasInsightsLicense) // hide insights link without a VI license
      .map((item) =>
        item.name === "settings"
          ? { ...item, subItems: max1024w ? mobileSettingsLink : settingsLinks } // replace subItems
          : item
      );

    setUpdatedNavItems(newNavItems);
  }, [max1024w, hasInsightsLicense]);

  // Trigger toast once when status starts
  useEffect(() => {
    if (uploadMutation.isSuccess && uploadMutation.data) {
      setCaseUploadStatus(uploadMutation.data);
    }

    if (uploadMutation.isPending || uploadMutation.isError) {
      ShowCaseUploadToast({ percent: uploadProgress, uploadMutation });
      setModalOpen(false);
      setModalType("");
    }
  }, [
    uploadMutation.isSuccess,
    uploadMutation.data,
    uploadMutation.isPending,
    uploadMutation.isError,
    uploadProgress,
  ]);

  // trigger toast on org profile query error
  useEffect(() => {
    if (orgProfileQuery.isError) {
      toast(
        t("toast.orgProfile.error", { message: orgProfileQuery.error.message })
      );
    }
  }, [orgProfileQuery.isError]);

  const handleExpand = (name: string) => {
    if (expandedNav !== name) {
      setExpandedNav(name);
    } else {
      setExpandedNav(null);
    }
  };

  const handlePageSelect = (route: string) => {
    if (locale) {
      router.push(`/${locale}${route}`);
    } else {
      router.push(`/en-US${route}`);
    }
    if (patientEnrollment) setPatientEnrollment(null);
    if (max1024w) setIsOverlay("");
  };

  const getRouteForItem = (
    item: Pick<INavItem, "license" | "licensedRoute" | "route">
  ): string => {
    if (item.license) {
      return userLicenses && item.license in userLicenses
        ? item.route
        : (item.licensedRoute ?? item.route);
    }
    return item.route;
  };

  const handlePatientRoute = (route: string) => {
    const newPath = pathname.replace(/\/[^\/]+$/, `/${route}`);
    router.push(newPath);

    if (max1024w) setIsOverlay("");
  };

  const returnToPatientPath = () => {
    handleReturnToPatientPath();
    const segments = pathname.split("/").filter(Boolean);
    const basePath = "/" + segments.slice(0, segments.length - 3).join("/");
    router.push(basePath);
  };

  const getSelectedItem = (name: string) => {
    const formattedTitle = name.indexOf("\n")
      ? name.replace(/\n/g, "-").replace(" ", "-")
      : name;

    return pathname!.includes(`/${formattedTitle.toLowerCase()}`);
  };

  if (pathname.includes(`/${locale}/login`)) return null;
  // /legal is a public page with its own header — suppress the app nav.
  if (pathname.includes("/legal")) return null;

  return sessionExpired ? null : pathname.includes("/admin/") ? (
    <AdminHeadNav />
  ) : !pathname.includes("/patient/") ? (
    <>
      {!max1024w ? (
        <NavContainer>
          <NavigationContainer
            $bckgColor={theme.white}
            style={{ minHeight: "62px" }}
          >
            <MultiOrgDropdown />
            {auth.isAuthenticated && (
              <div className="navlinks">
                {updatedNavItems.map((item) => {
                  const renderPrimaryItem = (item: INavItem) => (
                    <Link
                      href={
                        item.name !== "settings"
                          ? item.license
                            ? userLicenses && item.license in userLicenses
                              ? `/${locale}${item.route}`
                              : `/${locale}${item.licensedRoute}`
                            : `/${locale}${item.route}`
                          : ""
                      }
                      key={item.name}
                      passHref
                    >
                      <ClickableContainer
                        onClick={() => {
                          if (patientEnrollment) setPatientEnrollment(null);
                        }}
                      >
                        <PrimaryItem
                          className={`${getSelectedItem(item.name) ? "selected" : ""}`}
                        >
                          <p className="nav-p johnson-display">
                            {t(item.text)}
                          </p>
                          {item.name === "settings" && (
                            <Icon
                              url={ChevronIcon.src}
                              color={theme.main}
                              height="18px"
                              width="18px"
                              customClass={`${settingsHovered ? "settings-hovered" : ""}`}
                            />
                          )}
                        </PrimaryItem>
                      </ClickableContainer>
                    </Link>
                  );

                  if (item.name !== "settings") {
                    return renderPrimaryItem(item);
                  }

                  return (
                    <div
                      key={item.name}
                      onMouseEnter={() => setSettingsHovered(true)}
                      onMouseLeave={() => setSettingsHovered(false)}
                    >
                      {renderPrimaryItem(item)}
                      {settingsHovered && (
                        <SettingsHover>
                          {settingsLinks.map((subItem) => {
                            if (subItem.name === "logout") {
                              return (
                                <PrimaryItem
                                  key={subItem.name}
                                  onClick={() => signOutRedirect()}
                                  onMouseEnter={() => setSettingsHovered(true)}
                                  className={`${
                                    getSelectedItem(subItem.name)
                                      ? "selected"
                                      : ""
                                  }`}
                                >
                                  {/* <Image
                                    src={subItem.icon.src}
                                    alt="nav-icon"
                                    width={15}
                                    height={15}
                                    style={{ height: "auto" }}
                                  /> */}
                                  <p
                                    className="nav-p johnson-display"
                                    style={
                                      subItem.name === "logout"
                                        ? { color: theme.black }
                                        : undefined
                                    }
                                  >
                                    {t(subItem.text)}
                                  </p>
                                </PrimaryItem>
                              );
                            }
                            return (
                              <Link
                                href={`/${locale}${subItem.route}`}
                                key={subItem.name}
                                passHref
                              >
                                <PrimaryItem
                                  onClick={() => {
                                    if (patientEnrollment)
                                      setPatientEnrollment(null);
                                  }}
                                  onMouseEnter={() => setSettingsHovered(true)}
                                  className={`${
                                    getSelectedItem(subItem.name)
                                      ? "selected"
                                      : ""
                                  }`}
                                >
                                  {/* <Image
                            src={subItem.icon.src}
                            alt="nav-icon"
                            width={15}
                            height={15}
                            style={{ height: "auto" }}
                          /> */}
                                  <p
                                    className="nav-p johnson-display"
                                    style={
                                      subItem.name === "logout"
                                        ? { color: theme.black }
                                        : undefined
                                    }
                                  >
                                    {t(subItem.text)}
                                  </p>
                                </PrimaryItem>
                              </Link>
                            );
                          })}
                        </SettingsHover>
                      )}
                    </div>
                  );
                })}
                {/* Notifications */}
                <Notifications />
                {/* locale dropdown */}
                <LocaleDropdown
                  options={SUPPORTED_LOCALES}
                  selectedOption={selectedLocale}
                  onSelect={(e) => {
                    const value = (e.target as HTMLLIElement).getAttribute(
                      "data-value"
                    );
                    setSelectedLocale(value!);
                  }}
                  color={theme.main}
                  backgroundColor={theme.white}
                  styles={{ marginRight: 20 }}
                />
              </div>
            )}
          </NavigationContainer>

          {pathname!.includes("insights") && (
            <NavigationContainer
              $bckgColor={theme.lightGrey}
              style={{ minHeight: "44px", padding: "0 !important" }}
            >
              <div className="navlinks">
                {insightsLinks.map((item) => (
                  <Link
                    href={`/${locale}${item.route}`}
                    key={item.name}
                    passHref
                  >
                    <ClickableContainer
                      onClick={() => {
                        if (patientEnrollment) setPatientEnrollment(null);
                      }}
                    >
                      <SecondaryItem
                        className={`${getSelectedItem(item.name) && "selected"} `}
                      >
                        <p>{t(item.text)}</p>
                      </SecondaryItem>
                    </ClickableContainer>
                  </Link>
                ))}
              </div>
              {user_profile?.surgeon_npi && (
                <div className="secondary-functions">
                  <Button
                    label={t("componentText.uploadCaseData")}
                    icon={UploadIcon.src}
                    name="upload-case-data"
                    theme="secondary"
                    onPress={() => {
                      setModalOpen(true);
                      setModalType("upload-case-data");
                    }}
                  />
                  <Button
                    label={t("componentText.downloadCaseData")}
                    icon={
                      downloadMutation.isPending ? "loader" : DownloadIcon.src
                    }
                    iconColor={theme.white}
                    name="download-case-data"
                    theme="primary"
                    onPress={() => downloadMutation.mutate()}
                    isDisabled={downloadMutation.isPending}
                  />
                </div>
              )}
            </NavigationContainer>
          )}
          {/* this is where we would check if we have selected a patient on VPP */}
          {pathname!.includes("patientpath") && (
            <NavigationContainer
              $bckgColor={theme.lightGrey}
              style={{ minHeight: "44px", padding: "0 !important" }}
            >
              {!patientEnrollment ? (
                <>
                  <div className="navlinks" style={{ paddingLeft: "30px" }}>
                    {/* not for V1 */}
                    {/* <ToggleButton
                      options={[
                        t("componentText.viewActive"),
                        t("componentText.viewArchived"),
                      ]}
                      selectedOption={patientType}
                      onPressHandler={setPatientType}
                    /> */}
                  </div>
                  {canAddPatient && (
                    <div className="secondary-functions">
                      <Button
                        label={t("componentText.addPatient")}
                        icon={AddIcon.src}
                        name="add-patient"
                        theme="primary"
                        onPress={() => handleAddPatient()}
                      />
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="navlinks">
                    {patientPathLinks.map((item) => (
                      <Link
                        href={`/${locale}/provider/patientpath/enrollments/${enrollmentId}/${item.route}`}
                        key={item.name}
                        passHref
                      >
                        <ClickableContainer>
                          <SecondaryItem
                            className={`${getSelectedItem(item.name) && "selected"} `}
                          >
                            <p>{t(item.text)}</p>
                          </SecondaryItem>
                        </ClickableContainer>
                      </Link>
                    ))}
                  </div>
                  <div className="secondary-functions">
                    <Button
                      label={t("componentText.editPatient")}
                      icon={EditIcon.src}
                      name="edit-selected-enrollment"
                      theme="secondary"
                      onPress={() => handleEditPatient()}
                    />
                    <Button
                      label={t("componentText.returnDashboard")}
                      icon={ReturnIcon.src}
                      name="return-to-dashboard"
                      theme="primary"
                      onPress={returnToPatientPath}
                    />
                  </div>
                </>
              )}
            </NavigationContainer>
          )}
          {pathname.includes("settings") && (
            <NavigationContainer
              $bckgColor={theme.lightGrey}
              style={{ minHeight: "44px" }}
            >
              <div className="navlinks">
                {subSettingsLinks.map((item) => (
                  <Link
                    href={`/${locale}${item.route}`}
                    key={item.name}
                    passHref
                  >
                    <ClickableContainer
                      onClick={() => {
                        if (patientEnrollment) setPatientEnrollment(null);
                      }}
                    >
                      <SecondaryItem
                        key={item.name}
                        className={`${getSelectedItem(item.name) && "selected"} `}
                      >
                        <p>{t(item.text)}</p>
                      </SecondaryItem>
                    </ClickableContainer>
                  </Link>
                ))}
              </div>
            </NavigationContainer>
          )}
        </NavContainer>
      ) : (
        <NavContainer>
          <NavigationContainer $bckgColor={theme.white}>
            <div className="mobile-container">
              {auth.isAuthenticated && (
                <Icon
                  url={MenuIcon.src}
                  color={theme.main}
                  height="20px"
                  width="20px"
                  onPress={() => setIsOverlay("navigation")}
                />
              )}
              <Image
                src={DePuySynthesLogo.src}
                alt="DePuy Synthes"
                width={204}
                height={31}
                style={{ height: "31px", width: "auto" }}
              />
            </div>
            {auth.isAuthenticated && (
              <div className="mobile-container">
                {filterComponent && (
                  <Icon
                    url={FilterIcon.src}
                    color={theme.main}
                    height="20px"
                    width="20px"
                    onPress={() => setIsOverlay("filters")}
                  />
                )}

                {/* Notifications */}
                <Notifications />
                <Icon
                  url={LogoutIcon.src}
                  color={theme.main}
                  height="20px"
                  width="20px"
                  onPress={() => signOutRedirect()}
                />
              </div>
            )}
          </NavigationContainer>
          {isOverlay === "navigation" && (
            <>
              <MobileOverlay onClick={() => setIsOverlay("")} />
              <MobileNavigationOverlay
                className={`menu ${MobileOverlay !== "" && "open"}`}
              >
                <div
                  className="mobile-container"
                  style={{
                    width: "100%",
                    borderBottom: `1px solid ${theme.main}`,
                    padding: "15px 10px 10px 10px",
                    justifyContent: "space-between",
                  }}
                >
                  <div className="mobile-container">
                    <Icon
                      url={CloseIcon.src}
                      color={theme.main}
                      height="20px"
                      width="20px"
                      onPress={() => setIsOverlay("")}
                    />
                    <Image
                      src={DePuySynthesLogo.src}
                      alt="DePuy Synthes"
                      width={204}
                      height={31}
                      style={{ height: "31px", width: "auto" }}
                    />
                  </div>

                  {/* locale dropdown */}
                  <LocaleDropdown
                    options={SUPPORTED_LOCALES}
                    selectedOption={selectedLocale}
                    onSelect={(e) => {
                      const value = (e.target as HTMLLIElement).getAttribute(
                        "data-value"
                      );
                      setSelectedLocale(value!);
                    }}
                    color={theme.main}
                    backgroundColor={theme.white}
                  />
                </div>
                <MultiOrgDropdown />
                <div
                  className="items"
                  style={max1024w ? { marginBottom: "20px" } : {}}
                >
                  {updatedNavItems.map((item) => (
                    <div className="items" key={item.name}>
                      <PrimaryItem
                        onClick={() =>
                          item.subItems &&
                          (!item.license ||
                            !!(userLicenses && item.license in userLicenses)) &&
                          (item.name !== "patientpath" || patientEnrollment)
                            ? handleExpand(item.name)
                            : handlePageSelect(getRouteForItem(item))
                        }
                        className={`${
                          (item.subItems === undefined ||
                            (pathname.includes("patientpath") &&
                              !patientEnrollment)) &&
                          getSelectedItem(item.name) &&
                          "selected"
                        }`}
                      >
                        <p className="nav-p johnson-display">{t(item.text)}</p>
                        {item.subItems &&
                          (!item.license ||
                            !!(userLicenses && item.license in userLicenses)) &&
                          (item.name !== "patientpath" ||
                            patientEnrollment) && (
                            <Image
                              src={ChevronIcon.src}
                              alt="open-icon"
                              className={`${
                                expandedNav === item.name && "opened-chevron"
                              }`}
                              width={13}
                              height={13}
                              style={{
                                marginLeft: "10px",
                                transition: "all 300ms ease-in-out",
                                width: "auto",
                              }}
                            />
                          )}
                      </PrimaryItem>
                      {expandedNav === item.name && (
                        <div className="items">
                          {item.subItems!.map((subItem) => {
                            return (
                              <SecondaryItem
                                key={subItem.name}
                                onClick={() => {
                                  if (subItem.name === "logout") {
                                    signOutRedirect();
                                  } else if (item.name !== "patientpath") {
                                    handlePageSelect(getRouteForItem(subItem));
                                  } else {
                                    handlePatientRoute(subItem.route);
                                  }
                                }}
                                className={`${
                                  getSelectedItem(subItem.name) && "selected"
                                }`}
                              >
                                <p className="johnson-display">
                                  {t(subItem.text)}
                                </p>
                              </SecondaryItem>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </MobileNavigationOverlay>
            </>
          )}
          {isOverlay === "filters" && (
            <>
              <MobileOverlay onClick={() => setIsOverlay("")} />
              <MobileNavigationOverlay
                className={`filter ${MobileOverlay !== "" && "open"}`}
              >
                <div
                  className="mobile-container"
                  style={{
                    width: "100%",
                    borderBottom: `1px solid ${theme.main}`,
                    padding: "15px 10px 10px 10px",
                  }}
                >
                  <Icon
                    url={CloseIcon.src}
                    color={theme.main}
                    height="20px"
                    width="20px"
                    onPress={() => setIsOverlay("")}
                  />
                  <p className="nav-p">Filters</p>
                </div>
                <MobileFilters>{filterComponent}</MobileFilters>
              </MobileNavigationOverlay>
            </>
          )}
        </NavContainer>
      )}
      {isModalOpen && modalType === "upload-case-data" && (
        <Modal
          title={
            !viewingHelp ? tModals("uploadCaseData") : tModals("whereDoIFind")
          }
          content={<UploadCaseDataContent uploadMutation={uploadMutation} />}
          toggleModal={() => {
            setModalOpen(false);
            setModalType("");
          }}
        />
      )}
      {max768w &&
        isModalOpen &&
        (modalType === "view-patient-data" || modalType === "view-images") && (
          <NavContainer>
            <ModalMobileNavContainer $bckgColor={theme.white}>
              <div className="mobile-container">
                <div className="modal-mobile-container">
                  <Icon
                    color={theme.main}
                    url={CloseIcon.src}
                    width="20px"
                    height="20px"
                    onPress={() => handleCloseViewPatient()}
                  />
                  <h2>
                    {surgery && surgery.first_name && surgery.last_name
                      ? [
                          surgery.first_name,
                          surgery.middle_name,
                          surgery.last_name,
                        ].join(" ")
                      : tTable("emptyData.unknown")}
                  </h2>
                </div>
              </div>
              {modalType === "view-patient-data" && (
                <Icon
                  color={theme.main}
                  url={DownloadIcon.src}
                  width="25px"
                  height="25px"
                  onPress={() => downloadPatientCSV(surgery?.csv_key)}
                />
              )}
            </ModalMobileNavContainer>
          </NavContainer>
        )}
    </>
  ) : (
    <>
      <PatientHeadNav />
    </>
  );
}
